function formatBundleMoney(cents) {
  const dollars = (cents / 100).toFixed(2);
  const [whole, decimal] = dollars.split('.');
  const wholeWithSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${wholeWithSeparators}.${decimal}`;
}

function applyDiscount(price, percent) {
  if (percent <= 0) return price;
  return price - Math.round((price * percent) / 100);
}

/*
 * Single funnel-tracking entry point for the whole Recommended Add-ons feature.
 * Every interaction (feature view, learn-more click, option-modal open, add-on
 * select/deselect, bundle add-to-cart + outcome) routes through here so there's
 * one place to see the event surface and one place a consumer has to hook.
 *
 * Fires to two sinks, each optional:
 *   - window.dataLayer  -> Google Tag Manager, if a container is on the storefront
 *   - Shopify.analytics.publish -> a "Customer events" Custom Pixel that does
 *     `analytics.subscribe('bundle_<name>', ...)` (see the pixel snippet in the
 *     repo docs / PR description)
 * Both calls are guarded; nothing here throws if neither sink exists.
 */
function trackBundleEvent(name, detail) {
  const payload = { ...detail };

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  } catch (error) {
    /* dataLayer unavailable */
  }

  try {
    window.Shopify?.analytics?.publish(name, payload);
  } catch (error) {
    /* Shopify analytics unavailable */
  }
}

// Add-on identity/state read off a `.bundle-card__checkbox`. variant id/price are
// empty/0 for a multi-variant add-on until a choice is confirmed in its modal.
function bundleAddonContext(checkbox) {
  if (!checkbox) return {};
  return {
    addon_product_id: checkbox.dataset.addonProductId || '',
    addon_handle: checkbox.dataset.addonHandle || '',
    addon_title: checkbox.dataset.addonTitle || '',
    addon_variant_id: checkbox.dataset.variantId || '',
    addon_price: parseInt(checkbox.dataset.price, 10) || 0,
    discount_percent: parseFloat(checkbox.dataset.discountPercent) || 0,
  };
}

function bundleSourceContext(builder) {
  return {
    source_product_id: builder?.dataset.mainProductId || '',
    source_variant_id: builder?.dataset.mainVariantId || '',
  };
}

if (!customElements.get('bundle-variant-modal')) {
  customElements.define(
    'bundle-variant-modal',
    class BundleVariantModal extends ModalDialog {
      constructor() {
        super();

        this.variants = this.parseVariants();
        this.optionFieldsets = Array.from(this.querySelectorAll('[data-bundle-option]'));
        this.optionInputs = Array.from(this.querySelectorAll('[data-bundle-option-input]'));
        this.confirmButton = this.querySelector('[data-bundle-confirm]');
        this.removeButton = this.querySelector('[data-bundle-remove]');
        this.priceElement = this.querySelector('[data-bundle-modal-price]');
        this.originalPriceElement = this.querySelector('[data-bundle-modal-price-original]');
        this.discountAmountElement = this.querySelector('[data-bundle-modal-discount-amount]');
        this.helperElement = this.querySelector('[data-bundle-modal-helper]');
        this.currentVariant = null;
        this.discountPercent = parseFloat(document.getElementById(this.dataset.checkboxId)?.dataset.discountPercent) || 0;
        this.discountAmountTemplate = this.dataset.discountAmountTemplate || '';

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-bundle-option-input]')) this.updateAvailability();
        });

        this.confirmButton?.addEventListener('click', this.handleConfirm.bind(this));
        this.removeButton?.addEventListener('click', this.handleRemove.bind(this));
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent || '[]');
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      trackModalEvent(name) {
        // ModalDialog.connectedCallback reparents this element to <body>, so the
        // source product/variant come from the modal's own data attributes rather
        // than a DOM-ancestry lookup.
        const checkbox = document.getElementById(this.dataset.checkboxId);
        trackBundleEvent(name, {
          source_product_id: this.dataset.sourceProductId || '',
          source_variant_id: this.dataset.sourceVariantId || '',
          ...bundleAddonContext(checkbox),
          addon_variant_type: 'multi',
        });
      }

      show(opener) {
        super.show(opener);
        this.syncFromCheckbox();
        this.updateAvailability();
        this.trackModalEvent('bundle_option_modal_open');
      }

      get selectedValues() {
        const values = [];
        this.optionFieldsets.forEach((fieldset) => {
          const position = parseInt(fieldset.dataset.optionPosition, 10);
          const checked = fieldset.querySelector('[data-bundle-option-input]:checked');
          values[position - 1] = checked ? checked.value : undefined;
        });
        return values;
      }

      findMatchingVariant(values) {
        if (values.length === 0 || values.some((value) => value === undefined)) return null;
        return (
          this.variants.find((variant) => variant.options.every((value, index) => value === values[index])) || null
        );
      }

      syncFromCheckbox() {
        const checkbox = document.getElementById(this.dataset.checkboxId);
        const variantId = checkbox?.dataset.variantId;
        const variant = variantId ? this.variants.find((item) => String(item.id) === String(variantId)) : null;

        this.optionInputs.forEach((input) => {
          input.checked = false;
        });

        if (variant) {
          variant.options.forEach((value, index) => {
            const input = this.optionInputs.find(
              (candidate) => Number(candidate.dataset.optionPosition) === index + 1 && candidate.value === value
            );
            if (input) input.checked = true;
          });
        }

        if (this.removeButton) this.removeButton.hidden = !variant;
      }

      updateAvailability() {
        const current = this.selectedValues;

        this.optionFieldsets.forEach((fieldset) => {
          const position = parseInt(fieldset.dataset.optionPosition, 10);
          fieldset.querySelectorAll('[data-bundle-option-input]').forEach((input) => {
            const candidate = current.slice();
            candidate[position - 1] = input.value;
            const isAvailable = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options.every((value, index) => candidate[index] === undefined || value === candidate[index])
            );
            input.disabled = !isAvailable;
          });
        });

        const variant = this.findMatchingVariant(current);
        this.currentVariant = variant && variant.available ? variant : null;

        const displayPrice = this.currentVariant
          ? applyDiscount(this.currentVariant.price, this.discountPercent)
          : null;

        if (this.priceElement) {
          this.priceElement.textContent = displayPrice !== null ? formatBundleMoney(displayPrice) : '';
        }

        if (this.originalPriceElement) {
          const showOriginal = Boolean(this.currentVariant) && this.discountPercent > 0;
          this.originalPriceElement.textContent = showOriginal ? formatBundleMoney(this.currentVariant.price) : '';
          this.originalPriceElement.hidden = !showOriginal;
        }

        if (this.discountAmountElement) {
          const showDiscount = Boolean(this.currentVariant) && this.discountPercent > 0 && this.discountAmountTemplate;
          this.discountAmountElement.textContent = showDiscount
            ? this.discountAmountTemplate.replace(
                '[amount]',
                formatBundleMoney(this.currentVariant.price - displayPrice)
              )
            : '';
          this.discountAmountElement.hidden = !showDiscount;
        }

        if (this.confirmButton) {
          const ready = Boolean(this.currentVariant);
          this.confirmButton.disabled = !ready;
          this.confirmButton.setAttribute('aria-disabled', String(!ready));
        }

        if (this.helperElement) {
          const missingSelection = current.some((value) => value === undefined);
          if (missingSelection) {
            this.helperElement.textContent = this.helperElement.dataset.selectPrompt || '';
          } else if (!this.currentVariant) {
            this.helperElement.textContent = this.helperElement.dataset.unavailablePrompt || '';
          } else {
            this.helperElement.textContent = '';
          }
        }
      }

      applyCardDisplay(checkbox, variantLabelText, priceText, originalPriceText = '', discountAmountText = '') {
        const card = checkbox.closest('.bundle-card');
        if (!card) return;

        const variantLabel = card.querySelector('[data-bundle-variant-label]');
        if (variantLabel) {
          variantLabel.textContent = variantLabelText;
          variantLabel.hidden = !variantLabelText;
        }

        const priceElement = card.querySelector('[data-bundle-price]');
        if (priceElement) priceElement.textContent = priceText;

        const originalPriceElement = card.querySelector('[data-bundle-price-original]');
        if (originalPriceElement) {
          originalPriceElement.textContent = originalPriceText;
          originalPriceElement.hidden = !originalPriceText;
        }

        const discountAmountElement = card.querySelector('[data-bundle-discount-amount]');
        if (discountAmountElement) {
          discountAmountElement.textContent = discountAmountText;
          discountAmountElement.hidden = !discountAmountText;
        }
      }

      handleConfirm() {
        if (!this.currentVariant) return;

        const checkbox = document.getElementById(this.dataset.checkboxId);
        if (!checkbox) return;

        checkbox.dataset.variantId = this.currentVariant.id;
        checkbox.dataset.price = this.currentVariant.price;
        checkbox.checked = true;

        const displayPrice = applyDiscount(this.currentVariant.price, this.discountPercent);
        const originalPriceText =
          this.discountPercent > 0 ? `+${formatBundleMoney(this.currentVariant.price)}` : '';
        const discountAmountText =
          this.discountPercent > 0 && this.discountAmountTemplate
            ? this.discountAmountTemplate.replace(
                '[amount]',
                formatBundleMoney(this.currentVariant.price - displayPrice)
              )
            : '';

        this.applyCardDisplay(
          checkbox,
          this.currentVariant.title,
          `+${formatBundleMoney(displayPrice)}`,
          originalPriceText,
          discountAmountText
        );

        this.trackModalEvent('bundle_addon_selected');
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        this.hide();
      }

      handleRemove() {
        const checkbox = document.getElementById(this.dataset.checkboxId);
        if (!checkbox) return;

        // Track before the variant id/price are cleared below so the event
        // still carries which variant was removed.
        this.trackModalEvent('bundle_addon_deselected');

        checkbox.checked = false;
        checkbox.dataset.variantId = '';
        checkbox.dataset.price = '';

        const card = checkbox.closest('.bundle-card');
        const fromPriceText = card?.querySelector('[data-bundle-price]')?.dataset.fromPriceText || '';
        const fromOriginalPriceText = card?.querySelector('[data-bundle-price-original]')?.dataset.fromPriceText || '';
        const fromDiscountAmountText = card?.querySelector('[data-bundle-discount-amount]')?.dataset.fromPriceText || '';
        this.applyCardDisplay(checkbox, '', fromPriceText, fromOriginalPriceText, fromDiscountAmountText);

        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        this.hide();
      }
    }
  );
}

if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.mainVariantId = this.dataset.mainVariantId;
        this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
        this.errorMessage = this.dataset.errorMessage || '';
        this.discountNoteTemplate = this.dataset.discountNoteTemplate || '';
        this.checkboxes = Array.from(this.querySelectorAll('.bundle-card__checkbox'));
        this.totalPriceElement = this.querySelector('[data-bundle-total-price]');
        this.originalTotalElement = this.querySelector('[data-bundle-total-original]');
        this.saveNoteElement = this.querySelector('[data-bundle-save-note]');
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.errorElement = this.querySelector('[data-bundle-error]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.checkboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', this.onCheckboxChange.bind(this));
          this.syncCardQty(checkbox);
        });

        // Per-add-on quantity steppers. These sit inside the card's
        // <label>/<modal-opener>, so stop the click from bubbling out and
        // toggling the add-on or opening the variant modal.
        this.querySelectorAll('[data-bundle-qty]').forEach((stepper) => {
          const onStep = (delta) => (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.changeQty(stepper, delta);
          };
          stepper.querySelector('[data-bundle-qty-down]')?.addEventListener('click', onStep(-1));
          stepper.querySelector('[data-bundle-qty-up]')?.addEventListener('click', onStep(1));
        });

        // "Learn more" sits inside the card's <label>/<modal-opener>; stop the click
        // there so it opens the product tab without toggling the addon or opening
        // the variant modal.
        this.querySelectorAll('[data-bundle-learn-more]').forEach((link) => {
          link.addEventListener('click', (event) => {
            event.stopPropagation();
            const checkbox = link.closest('.bundle-card')?.querySelector('.bundle-card__checkbox');
            trackBundleEvent('bundle_learn_more_click', {
              ...bundleSourceContext(this),
              ...bundleAddonContext(checkbox),
            });
          });
        });

        this.submitButton?.addEventListener('click', this.onSubmit.bind(this));

        this.updateTotal();

        trackBundleEvent('bundle_view', {
          ...bundleSourceContext(this),
          addon_count_offered: this.checkboxes.length,
        });
      }

      // Quantity is stored on the checkbox as data-quantity, always an integer >= 1.
      static getQty(checkbox) {
        return Math.max(1, parseInt(checkbox?.dataset.quantity, 10) || 1);
      }

      // Show the stepper only while the add-on is selected; reset to 1 when it is
      // deselected so a later re-select doesn't inherit a stale count.
      syncCardQty(checkbox) {
        const card = checkbox.closest('.bundle-card');
        const stepper = card?.querySelector('[data-bundle-qty]');
        if (!stepper) return;

        if (!checkbox.checked) {
          checkbox.dataset.quantity = '1';
        }
        const value = stepper.querySelector('[data-bundle-qty-value]');
        if (value) value.textContent = BundleBuilder.getQty(checkbox);
        stepper.hidden = !checkbox.checked;
      }

      changeQty(stepper, delta) {
        const card = stepper.closest('.bundle-card');
        const checkbox = card?.querySelector('.bundle-card__checkbox');
        if (!checkbox || !checkbox.checked) return;

        const next = Math.min(99, Math.max(1, BundleBuilder.getQty(checkbox) + delta));
        if (next === BundleBuilder.getQty(checkbox)) return;

        checkbox.dataset.quantity = String(next);
        const value = stepper.querySelector('[data-bundle-qty-value]');
        if (value) value.textContent = next;

        this.updateTotal();
        trackBundleEvent('bundle_addon_quantity_change', {
          ...bundleSourceContext(this),
          ...bundleAddonContext(checkbox),
          addon_quantity: next,
        });
      }

      onCheckboxChange(event) {
        this.updateTotal();

        const checkbox = event?.target;
        if (!checkbox) return;

        this.syncCardQty(checkbox);

        // Multi-variant add-ons are tracked from their variant modal (confirm /
        // remove), which also dispatches this change event - skip them here so
        // selections aren't counted twice.
        if (checkbox.closest('.bundle-card--multi')) return;

        trackBundleEvent(checkbox.checked ? 'bundle_addon_selected' : 'bundle_addon_deselected', {
          ...bundleSourceContext(this),
          ...bundleAddonContext(checkbox),
          addon_variant_type: 'single',
        });
      }

      get selectedCheckboxes() {
        // A multi-variant addon's checkbox only carries a usable variant id/price once a
        // choice has been confirmed in its bundle-variant-modal; guard against a checked
        // checkbox with no variant resolved yet (shouldn't happen via the UI, but keeps a
        // stray checked-with-empty-dataset state from reaching the cart payload/total).
        return this.checkboxes.filter(
          (checkbox) => checkbox.checked && !checkbox.disabled && checkbox.dataset.variantId
        );
      }

      computeTotals() {
        const selected = this.selectedCheckboxes;
        // The widget presents the whole bundle: the anchor product plus the
        // selected add-ons (each priced unit x quantity).
        const addonsTotal = selected.reduce((sum, checkbox) => {
          const price = parseInt(checkbox.dataset.price, 10) || 0;
          return sum + price * BundleBuilder.getQty(checkbox);
        }, 0);
        const fullTotal = this.mainPrice + addonsTotal;

        let discountAmount = 0;
        selected.forEach((checkbox) => {
          const percent = parseFloat(checkbox.dataset.discountPercent) || 0;
          if (percent <= 0) return;

          // The store's bundle discounts are all buy-1-get-1 automatic discounts,
          // so exactly one unit of each add-on is discounted - any extra units
          // the shopper adds with the stepper are full price.
          const unitPrice = parseInt(checkbox.dataset.price, 10) || 0;
          discountAmount += unitPrice - applyDiscount(unitPrice, percent);
        });

        return {
          selected,
          addonsTotal,
          fullTotal,
          discountAmount,
          total: fullTotal - discountAmount,
        };
      }

      updateTotal() {
        if (!this.totalPriceElement) return;

        const { fullTotal, discountAmount } = this.computeTotals();

        if (discountAmount > 0) {
          this.totalPriceElement.textContent = formatBundleMoney(fullTotal - discountAmount);

          if (this.originalTotalElement) {
            this.originalTotalElement.textContent = formatBundleMoney(fullTotal);
            this.originalTotalElement.hidden = false;
          }

          if (this.saveNoteElement) {
            this.saveNoteElement.textContent = this.discountNoteTemplate.replace(
              '[amount]',
              formatBundleMoney(discountAmount)
            );
            this.saveNoteElement.hidden = false;
          }
        } else {
          this.totalPriceElement.textContent = formatBundleMoney(fullTotal);

          if (this.originalTotalElement) this.originalTotalElement.hidden = true;
          if (this.saveNoteElement) this.saveNoteElement.hidden = true;
        }
      }

      setLoading(loading) {
        if (!this.submitButton) return;

        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.setAttribute('aria-disabled', loading);
        this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      showError(message) {
        if (!this.errorElement) return;

        if (message) {
          this.errorElement.textContent = message;
          this.errorElement.removeAttribute('hidden');
        } else {
          this.errorElement.setAttribute('hidden', '');
        }
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.showError('');
        this.setLoading(true);

        const { selected, addonsTotal, fullTotal, discountAmount, total } = this.computeTotals();
        const currency = window.Shopify?.currency?.active || 'AUD';
        const cartAnalytics = {
          ...bundleSourceContext(this),
          addon_count: selected.length,
          addon_unit_count: selected.reduce((sum, checkbox) => sum + BundleBuilder.getQty(checkbox), 0),
          addon_variant_ids: selected.map((checkbox) => checkbox.dataset.variantId).join(','),
          addon_titles: selected.map((checkbox) => checkbox.dataset.addonTitle || '').join(' | '),
          bundle_subtotal: fullTotal,
          bundle_discount: discountAmount,
          bundle_total: total,
          // value = the whole bundle (anchor + add-ons - discount), GA4's
          // "value of items added". addons_value = just the add-ons this widget
          // drove in, post per-add-on discount - the CRO attribution number.
          value: total / 100,
          addons_value: Math.max(0, addonsTotal - discountAmount) / 100,
          currency,
        };
        trackBundleEvent('bundle_add_to_cart', cartAnalytics);

        this.cartQuantities()
          .then((qtyInCart) => {
            // This widget configures a bundle, so re-using it should top the
            // cart up to what's shown - not stack another full copy. For each
            // line (anchor at qty 1, add-ons at their stepper qty) add only the
            // shortfall against what's already in the cart; never remove.
            const items = [];

            if (this.mainVariantId && qtyInCart(this.mainVariantId) < 1) {
              items.push({ id: this.mainVariantId, quantity: 1 });
            }

            selected.forEach((checkbox) => {
              const shortfall = BundleBuilder.getQty(checkbox) - qtyInCart(checkbox.dataset.variantId);
              if (shortfall > 0) {
                items.push({
                  id: checkbox.dataset.variantId,
                  quantity: shortfall,
                  properties: { _source: 'recommended-add-ons' },
                });
              }
            });

            if (items.length === 0) {
              // Cart already contains the whole bundle - nothing to add.
              window.location = routes.cart_url;
              return null;
            }

            const body = { items };

            if (this.cart) {
              body.sections = this.cart.getSectionsToRender().map((section) => section.id);
              body.sections_url = window.location.pathname;
              this.cart.setActiveElement(document.activeElement);
            }

            return fetch(`${routes.cart_add_url}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            })
              .then((response) => response.json())
              .then((response) => this.onAddResponse(response, selected, cartAnalytics));
          })
          .catch((error) => {
            console.error(error);
            this.showError(this.errorMessage);
            trackBundleEvent('bundle_add_to_cart_error', {
              ...cartAnalytics,
              error_message: String((error && error.message) || error || 'network_error'),
            });
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      // Resolves to a fn: variantId -> total quantity of that variant currently
      // in the cart (summed across lines, e.g. discount-split lines). On a fetch
      // failure it reports 0 for everything, so onSubmit falls back to adding the
      // full shown quantities rather than silently skipping the add.
      cartQuantities() {
        return fetch(`${routes.cart_url}.json`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            const totals = new Map();
            (cart.items || []).forEach((line) => {
              const key = String(line.variant_id);
              totals.set(key, (totals.get(key) || 0) + line.quantity);
            });
            return (variantId) => totals.get(String(variantId)) || 0;
          })
          .catch(() => () => 0);
      }

      onAddResponse(response, selected, cartAnalytics) {
        if (response.status) {
          const message = response.description || response.message || this.errorMessage;
          this.showError(message);
          trackBundleEvent('bundle_add_to_cart_error', { ...cartAnalytics, error_message: message });
          return;
        }

        trackBundleEvent('bundle_add_to_cart_success', cartAnalytics);

        // Order-level marker that this cart used the Recommended Add-ons
        // feature. Cart attributes carry into the order's customAttributes and
        // are returned by the Admin API. Kept un-prefixed (like the store's
        // existing `mc_cid` cart attribute) so the Admin API reliably returns
        // them; Shopify's default checkout / emails don't render cart note
        // attributes anyway.
        //
        // Only written when at least one add-on was selected: an anchor-only
        // submit isn't a feature conversion, and tagging it made 5 of the first
        // 7 tagged orders anchor-only. Cart attributes persist for the life of
        // the cart, so this still means "widget used with add-ons", not
        // "add-on purchased" — for revenue, count order lines carrying the
        // `_source` line-item property instead.
        const addonHandles = selected
          .map((checkbox) => checkbox.dataset.addonHandle)
          .filter(Boolean)
          .join(',');
        const tagCart = !addonHandles
          ? Promise.resolve()
          : fetch(`${routes.cart_update_url}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              // keepalive lets the request finish even if the page navigates away
              // before it resolves (below) — this store has no cart
              // drawer/notification (cart_type: "page"), so the success path
              // redirects to /cart, which otherwise cancels this write and the
              // order never gets tagged.
              keepalive: true,
              body: JSON.stringify({
                attributes: {
                  bundle_source: 'recommended-add-ons',
                  bundle_main_product: this.dataset.mainProductId || '',
                  bundle_addons: addonHandles,
                },
              }),
            }).catch(() => {
              /* attribute is best-effort; the add to cart already succeeded */
            });

        if (!this.cart) {
          // Wait for the tag write to settle before redirecting so it isn't
          // cancelled mid-flight, but don't hold the shopper on the PDP if the
          // request is slow — keepalive covers the timeout case.
          Promise.race([tagCart, new Promise((resolve) => setTimeout(resolve, 1500))]).finally(() => {
            window.location = routes.cart_url;
          });
          return;
        }

        this.cart.renderContents(response);
        if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-builder',
            cartData: response,
          });
        }
      }
    }
  );
}
